/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosResponse } from 'axios';
import { useCallback } from 'react';
import axios from './axios';
import { handleAxiosError, serverUrl } from './axios-service';
import { Horoscope, HoroscopeRequest, HoroscopeResponse } from '../types/horoscope';
import { NatalChartData, NatalChartDataRequest } from '../types/astrological-chart';

const baseUrl = `${serverUrl}/horoscopes`;

const useHoroscopeApi = () => {
  // Create new horoscope
  const createHoroscope = useCallback(async (data: HoroscopeRequest): Promise<Horoscope> => {
    try {
      const result: AxiosResponse<Horoscope> = await axios.post(baseUrl, data);
      return result.data;
    } catch (error: any) {
      return handleAxiosError(error);
    }
  }, []);

  // Get horoscope by ID
  const getHoroscopeById = useCallback(async (horoscopeId: string): Promise<Horoscope> => {
    try {
      const result: AxiosResponse<Horoscope> = await axios.get(`${baseUrl}/${horoscopeId}`);
      return result.data;
    } catch (error: any) {
      return handleAxiosError(error);
    }
  }, []);

  // Get all horoscopes for a user
  const getUserHoroscopes = useCallback(async (userId: string): Promise<Horoscope[]> => {
    try {
      const result: AxiosResponse<Horoscope[]> = await axios.get(`${baseUrl}/user/${userId}`);
      return result.data;
    } catch (error: any) {
      return handleAxiosError(error);
    }
  }, []);

  // Update horoscope
  const updateHoroscope = useCallback(async (horoscopeId: string, data: HoroscopeRequest): Promise<Horoscope> => {
    try {
      const result: AxiosResponse<Horoscope> = await axios.put(`${baseUrl}/${horoscopeId}`, data);
      return result.data;
    } catch (error: any) {
      return handleAxiosError(error);
    }
  }, []);

  // Delete horoscope
  const deleteHoroscope = useCallback(async (horoscopeId: string): Promise<void> => {
    try {
      await axios.delete(`${baseUrl}/${horoscopeId}`);
    } catch (error: any) {
      return handleAxiosError(error);
    }
  }, []);

  // Generate horoscope (existing endpoint)
  const generateHoroscope = useCallback(
    async (data: HoroscopeRequest): Promise<HoroscopeResponse> => {
      try {
        const result: AxiosResponse<HoroscopeResponse> = await axios.post(
          `${serverUrl}/horoscope-generation/generate`,
          data
        );
        return result.data;
      } catch (error: any) {
        return handleAxiosError(error);
      }
    },
    []
  );

  const generateHoroscopePersonalized = useCallback(
    async (data: HoroscopeRequest): Promise<HoroscopeResponse> => {
      try {
        const result: AxiosResponse<HoroscopeResponse> = await axios.post(
          `${serverUrl}/horoscope-generation/generate-personalized`,
          data
        );
        return result.data;
      } catch (error: any) {
        return handleAxiosError(error);
      }
    },
    []
  );

  // Get natal chart data
  const getNatalChartData = useCallback(
    async (request: NatalChartDataRequest): Promise<NatalChartData> => {
      try {
        const result: AxiosResponse<NatalChartData> = await axios.post(
          `${serverUrl}/horoscope-generation/natal-chart-data`,
          request
        );
        return result.data;
      } catch (error: any) {
        return handleAxiosError(error);
      }
    },
    []
  );

  return {
    createHoroscope,
    getHoroscopeById,
    getUserHoroscopes,
    updateHoroscope,
    deleteHoroscope,
    generateHoroscope,
    generateHoroscopePersonalized,
    getNatalChartData
  };
};

export default useHoroscopeApi; 